import { withZeroTransaction } from "../../zero/utils";
import { createInsertSchema, createUpdateSchema } from "../../utils/sql";
import { list, listSection, listItem } from "./list.sql";
import { z } from "zod/v4";
import { zql, type Schema } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import {
  type ExpressionBuilder,
  type Row,
  type Transaction,
  type Zero,
  type QueryRowType,
} from "@rocicorp/zero";
import { Food } from "../../nutrition/food";
import { accessDenied, sort } from "@corpuscomplete/core/utils";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";

/**
 * List namespace boundary for collaborative list CRUD and visibility checks.
 *
 * Invariants:
 * - Visibility/edit rights are granted by direct list grants or parent
 *   list-collection grants.
 * - Item/section ordering is maintained through stable `sortIndex` handling.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace List {
  // TODO: Paginate
  // TODO: Remove in favor of listing by collections?
  export const all = defineQuery(z.undefined(), ({ ctx }) =>
    zql.list.where((e) => viewable(e, ctx.actor)).related("items"),
  );
  export type Preview = QueryRowType<typeof all>;

  export function viewable(eb: ExpressionBuilder<"list", Schema>, actor: UserActor) {
    const { or, exists } = eb;
    return or(
      exists(
        "grantsByCollection",
        (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("list_collection", "contents", [
              "editor",
              "collaborator",
              "viewer",
            ]),
          ),
        {
          flip: true,
        },
      ),
      exists(
        "grants",
        (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("list", "root", ["editor", "collaborator", "viewer"]),
          ),
        {
          flip: true,
        },
      ),
    );
  }

  export async function canEdit(
    tx: Transaction,
    actor: UserActor,
    listId: string,
  ): Promise<boolean> {
    const byCollectionPromise = tx.run(
      zql.list
        .where("id", "=", listId)
        .whereExists(
          "grantsByCollection",
          (r) =>
            AccessGrant.matching(
              r,
              actor,
              AccessGrant.criteria("list_collection", "contents", ["editor"]),
            ),
          {
            flip: true,
          },
        )
        .one(),
    );
    const byListPromise = tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", listId),
        actor,
        AccessGrant.criteria("list", "root", ["editor"]),
      ).one(),
    );
    const [byCollection, byList] = await Promise.all([byCollectionPromise, byListPromise]);
    return Boolean(byCollection || byList);
  }

  export async function canCollaborate(
    tx: Transaction,
    actor: UserActor,
    listId: string,
  ): Promise<boolean> {
    const byCollectionPromise = tx.run(
      zql.list
        .where("id", "=", listId)
        .whereExists(
          "grantsByCollection",
          (r) =>
            AccessGrant.matching(
              r,
              actor,
              AccessGrant.criteria("list_collection", "contents", ["editor", "collaborator"]),
            ),
          {
            flip: true,
          },
        )
        .one(),
    );
    const byListPromise = tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", listId),
        actor,
        AccessGrant.criteria("list", "root", ["editor", "collaborator"]),
      ).one(),
    );
    const [byCollection, byList] = await Promise.all([byCollectionPromise, byListPromise]);
    return Boolean(byCollection || byList);
  }

  export async function canAddToCollection(
    tx: Transaction,
    actor: UserActor,
    collectionId: string,
  ): Promise<boolean> {
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", collectionId),
        actor,
        AccessGrant.criteria("list_collection", "contents", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  export const details = defineQuery(z.object({ id: z.string() }), ({ ctx, args }) =>
    zql.list
      .where((e) => viewable(e, ctx.actor))
      .where("id", "=", args.id)
      .related("sections", (q) => q.orderBy("sortIndex", "asc"))
      .related("items", (q) =>
        q
          .related("shoppingListFoodItem", (q) =>
            q.related("serving", (s) => s.related("food", (q) => q.related("meta"))),
          )
          .orderBy("sortIndex", "asc"),
      )
      .one(),
  );
  export type RawInfo = NonNullable<QueryRowType<typeof details>>;

  // // TODO: Try to get this working with local query again
  export const itemDetails = (z: Zero, id: string) =>
    zql.listItem
      .where("id", "=", id)
      .related("shoppingListFoodItem", (q) =>
        q.related("serving", (s) => s.related("food", (q) => q.related("meta"))),
      )
      .one();
  // // TODO: Is there any way to use this as part of the list details query?
  // export const itemDetails = syncedQuery(
  //   "List.itemDetails",
  //   z.tuple([z.object({ id: z.string() })]),
  //   ({ id }) =>
  //     builder.listItem
  //       .where("id", "=", id)
  //       .related("shoppingListFoodItem", (q) =>
  //         q.related("food").related("serving"),
  //       )
  //       .one(),
  // );

  export type Item = Row<Schema["tables"]["listItem"]> & {
    label: string;
  };
  export const asItem = (item: RawInfo["items"][number]): Item | null => {
    if (item.shoppingListFoodItem) {
      const foodItem = Food.asItem(item.shoppingListFoodItem);
      if (!foodItem) {
        return null;
      }
      const foodLabel = Food.formatItem(foodItem);
      return {
        ...item,
        label: `${foodLabel.subtitle} ${foodLabel.title}`,
      };
    }
    return {
      ...item,
      label: item.label ?? "",
    };
  };

  export type Section = Row<Schema["tables"]["listSection"]> & {
    items: readonly Item[];
  };

  export type Info = Row<Schema["tables"]["list"]> & {
    sections: readonly Section[];
    orphanItems: readonly Item[];
  };
  export function asInfo(raw: RawInfo): Info {
    const sections = new Map<string, Omit<Section, "items"> & { items: Item[] }>(
      raw.sections.map((section) => [section.id, { ...section, items: [] }]),
    );
    const orphanItems: Item[] = [];
    for (const rawItem of raw.items) {
      const item = asItem(rawItem);
      if (!item) {
        continue;
      }
      if (item.sectionId) {
        sections.get(item.sectionId)?.items.push(item);
      } else {
        orphanItems.push(item);
      }
    }
    return {
      ...raw,
      sections: sort(
        Array.from(sections.values()).map((section) => ({
          ...section,
          items: sort(section.items),
        })),
      ),
      orphanItems: sort(orphanItems),
    };
  }

  type ItemStatusCounts = Record<List.Item["status"] | "total", number>;

  export const itemStatusCounts = (list: List.Info | List.Preview): ItemStatusCounts => {
    const counts: ItemStatusCounts = {
      total: 0,
      pending: 0,
      in_progress: 0,
      complete: 0,
      canceled: 0,
    };
    const allItems =
      "sections" in list
        ? list.sections.flatMap((section) => section.items).concat(list.orphanItems)
        : list.items;
    for (const item of allItems) {
      counts[item.status]++;
      counts.total++;
    }
    return counts;
  };
  export const itemDonePercentage = (counts: ItemStatusCounts): number => {
    const done = counts.complete + counts.canceled;
    return (done / counts.total) * 100;
  };

  export const createItemSchema = createInsertSchema(listItem)
    .omit({
      listId: true,
      status: true,
      sectionId: true,
    })
    .extend({
      label: z.string().max(255).nullable(),
    });

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    createInsertSchema(list).extend({
      sections: createInsertSchema(listSection)
        .omit({ listId: true })
        .extend({ items: createItemSchema.array() })
        .array(),
      orphanItems: createItemSchema.array(),
    }),
    async ({ tx, ctx, args: { orphanItems, sections, ...input } }) => {
      const hasPermission = await canAddToCollection(tx, ctx.actor, input.collectionId);
      if (!hasPermission) {
        accessDenied("Cannot add list to collection");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.list.insert({
        ...input,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });

      await Promise.all(
        sections.map(async ({ items, ...section }) => {
          await tx.mutate.listSection.insert({
            ...section,
            listId: input.id,
            timeCreated: timeNow,
            timeUpdated: timeNow,
          });
          await Promise.all(
            items.map(async (item) => {
              await tx.mutate.listItem.insert({
                ...item,
                sectionId: section.id,
                listId: input.id,
                status: "pending",
                timeCreated: timeNow,
                timeUpdated: timeNow,
              });
            }),
          );
        }),
      );

      await Promise.all(
        orphanItems.map(async (item) => {
          await tx.mutate.listItem.insert({
            ...item,
            listId: input.id,
            status: "pending",
            timeCreated: timeNow,
            timeUpdated: timeNow,
          });
        }),
      );
    },
  );

  export type Update = z.infer<typeof update.schema>;
  export const update = withZeroTransaction(
    createUpdateSchema(list).omit({ type: true }).extend({ id: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const hasPermission = await canEdit(tx, ctx.actor, args.id);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.list.update({
        ...args,
        timeUpdated: timeNow,
      });
    },
  );

  export type Delete = z.infer<typeof del.schema>;
  export const del = withZeroTransaction(
    createInsertSchema(list).pick({ id: true }),
    async ({ tx, ctx, args: { id } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, id);
      if (!hasPermission) {
        accessDenied("Cannot delete list");
      }
      const list = await tx.run(
        zql.list.where("id", "=", id).related("sections").related("items").one(),
      );
      if (!list) {
        accessDenied("List not found");
      }
      await Promise.all([
        ...list.sections.map((section) => tx.mutate.listSection.delete({ id: section.id })),
        ...list.items.map((item) => tx.mutate.listItem.delete({ id: item.id })),
      ]);
      await tx.mutate.list.delete({ id });
    },
  );

  export const addSection = withZeroTransaction(
    createInsertSchema(listSection),
    async ({ tx, ctx, args }) => {
      const hasPermission = await canEdit(tx, ctx.actor, args.listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.listSection.insert({
        ...args,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );
  export type AddSection = z.infer<typeof addSection.schema>;

  export const updateSection = withZeroTransaction(
    createUpdateSchema(listSection).extend({ id: z.uuid(), listId: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const hasPermission = await canEdit(tx, ctx.actor, args.listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.listSection.update({
        ...args,
        timeUpdated: timeNow,
      });
    },
  );
  export type UpdateSection = z.infer<typeof updateSection.schema>;

  export const removeSection = withZeroTransaction(
    createInsertSchema(listSection)
      .pick({ id: true, listId: true })
      .extend({
        itemAction: z.enum(["delete", "orphan"]),
      }),
    async ({ tx, ctx, args: { id, listId, itemAction } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const section = await tx.run(zql.listSection.where("id", "=", id).related("items").one());
      if (!section) {
        accessDenied("List section not found");
      }
      await Promise.all(
        section.items.map(async (item) => {
          if (itemAction === "delete") {
            await tx.mutate.listItem.delete({ id: item.id });
          } else if (itemAction === "orphan") {
            await tx.mutate.listItem.update({ id: item.id, sectionId: null });
          }
        }),
      );
      await tx.mutate.listSection.delete({ id });
    },
  );
  export type RemoveSection = z.infer<typeof removeSection.schema>;

  export const addItem = withZeroTransaction(
    createInsertSchema(listItem).omit({ status: true }),
    async ({ tx, ctx, args }) => {
      const hasPermission = await canEdit(tx, ctx.actor, args.listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.listItem.insert({
        ...args,
        status: "pending",
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );
  export type AddItem = z.infer<typeof addItem.schema>;

  export const updateItem = withZeroTransaction(
    createUpdateSchema(listItem).omit({ status: true }).extend({ id: z.uuid(), listId: z.uuid() }),
    async ({ tx, ctx, args: { id, listId, ...item } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.listItem.update({ id, ...item, timeUpdated: timeNow });
    },
  );
  export type UpdateItem = z.infer<typeof updateItem.schema>;

  export const removeItem = withZeroTransaction(
    createInsertSchema(listItem).pick({
      id: true,
      listId: true,
    }),
    async ({ tx, ctx, args: { id, listId } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, listId);
      if (!hasPermission) {
        accessDenied("Cannot edit list");
      }
      await tx.mutate.listItem.delete({ id });
    },
  );
  export type RemoveItem = z.infer<typeof removeItem.schema>;

  export const updateItemStatus = withZeroTransaction(
    createInsertSchema(listItem).pick({ id: true, listId: true, status: true }),
    async ({ tx, ctx, args: { id, listId, status } }) => {
      const hasPermission = await canCollaborate(tx, ctx.actor, listId);
      if (!hasPermission) {
        accessDenied("Cannot collaborate on list");
      }
      const timeNow = Temporal.Now.instant();
      await tx.mutate.listItem.update({ id, status, timeUpdated: timeNow });
    },
  );
  export type UpdateItemStatus = z.infer<typeof updateItemStatus.schema>;
}
